/**
 * Manual credits and debits against a practice — §8's PRIMARY refund-after-payout tool,
 * fully implemented on the server and with no screen at all until now.
 *
 * *"Net against future earnings. Post a negative adjustment to `tenant_adjustments`. Deduct
 * from the next payout. Works if the tenant has ongoing volume."* Without a screen, an
 * operator recovering money after a payout had no route but a hand-written Mongo update.
 *
 * DIRECTION IS A RADIO, NOT A SIGN. The server takes a signed `amountCents` and treats the
 * sign as the direction, which means a stray minus is the difference between crediting and
 * clawing back four thousand dollars. The operator picks a direction in words, and the
 * acknowledgement they tick restates it: "I am debiting Clinique Rive-Sud 1,240.00 USD."
 *
 * Validation mirrors the server exactly (`admin.create_adjustment`: integer, non-zero,
 * reason ≥ 5 characters after strip) so the operator is never told by a 422 what the form
 * could have told them. Submit stays ENABLED and judges on click — the house rule — so the
 * form can point at the offending field instead of greying out for an unstated reason.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { toCents } from "../../admin/money";
import { useLanguage } from "../../lib/LanguageContext";
import { formatMoneyLedger } from "../../lib/formatMoney";
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { mintIdempotencyKey } from "../../gateway/api";
import { extractGatewayErrorCode } from "../../gateway/errors";
import StepUpConfirmDialog from "../../gateway/components/StepUpConfirmDialog";
import useFieldErrors from "../../hooks/useFieldErrors";
import { Criteria, MUTED, useFieldErrorText } from "./Ledger";
import { WriteError } from "./ResourceState";
import { adjustmentSchema } from "./schemas";
import { useScreenWrites } from "../screenWrites";

const BLANK = { tenantId: "", direction: "", amount: "", reason: "" };

// The adjustment's identity IS the ledger's natural key — see `mintIdempotencyKey`,
// which the refund dialog uses for exactly the same reason.
const mintKey = () => mintIdempotencyKey("adj");

export default function AdjustmentsScreen({ t, tc, gatewayApi }) {
  // The whole screen is one write (finance.write behind a step-up): without it, it says so.
  const { manage: canAdjust } = useScreenWrites("gateway");
  const { lang } = useLanguage();
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [writeCode, setWriteCode] = useState(undefined);
  const [idempotencyKey, setIdempotencyKey] = useState(null);
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  // NOT `tc` directly: `gw.valid.*` lives in the SUPERADMIN namespace while
  // `validation.tooLong` lives in COMMON, and resolving either with the wrong translator
  // prints the raw key. See `useFieldErrorText`.
  const ft = useFieldErrorText();
  // THE server's currency, not a fixtures constant (`gateway/contract.js:28` says
  // components must never assume it), read through the already-injected `gatewayApi`
  // rather than a new module import — `lib/useTenantCurrency.js` answers the PRACTICE's
  // currency, which is not this figure: an adjustment is a LEDGER entry and the ledger
  // books one currency. That module also hits `@/lib/api`
  // directly, which is unresolvable outside a test that shims it, and every screen in
  // this console already carries `gatewayApi` for exactly this kind of read. Empty while
  // unresolved; labels below omit the currency rather than naming one this screen guessed.
  const [settlementCurrency, setSettlementCurrency] = useState("");
  useEffect(() => {
    let alive = true;
    gatewayApi.adminGetSettings()
      .then((s) => { if (alive && s?.settlementCurrency) setSettlementCurrency(s.settlementCurrency); })
      .catch(() => {});
    return () => { alive = false; };
  }, [gatewayApi]);
  const currencyLabel = settlementCurrency.toUpperCase();

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); clearFieldError(k); };
  const cents = toCents(form.amount);
  const signed = form.direction === "debit" ? -cents : cents;

  const openConfirm = () => {
    setWriteCode(undefined);
    if (validate(adjustmentSchema, form)) return;   // the fields say why; no toast needed
    // One key per CONFIRMED INTENT, not per request: a retry after a failed step-up must
    // be the same money fact, and only a fresh trip through this form is a new one.
    setIdempotencyKey(mintKey());
    setOpen(true);
  };

  const onConfirm = async ({ stepUpToken }) => {
    try {
      // NO `currency`: the server applies its own `settings.settlementCurrency` default
      // (`modules/gateway/routers/admin.py`). Stating one here would let the client decide
      // the platform's settlement currency instead of merely displaying it.
      await gatewayApi.adminCreateAdjustment({
        tenantId: form.tenantId.trim(),
        idempotencyKey: idempotencyKey ?? mintKey(),
        amountCents: signed,
        reason: form.reason.trim(),
      }, stepUpToken);
      toast.success(t("gw.console.adjustments.recorded"));
      setForm(BLANK);
      setIdempotencyKey(null);
      return { ok: true };
    } catch (e) {
      const code = extractGatewayErrorCode(e);
      setWriteCode(code);
      return { ok: false, code };
    }
  };

  const err = (k) => ft(fieldErrors[k]);

  if (!canAdjust) {
    return (
      <div data-testid="adjustments-screen" className="space-y-4">
        <Criteria>{t("gw.console.adjustments.criteria")}</Criteria>
        <p role="status" className="text-sm text-slate-500" data-testid="adjustments-forbidden">{t("access.forbiddenAction")}</p>
      </div>
    );
  }

  return (
    <div data-testid="adjustments-screen" className="space-y-4">
      <Criteria>{t("gw.console.adjustments.criteria")}</Criteria>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
        <div>
          <Label className={MUTED}>{t("gw.console.adjustments.tenantId")}</Label>
          <Input value={form.tenantId} onChange={(e) => set("tenantId", e.target.value)}
                 className={fieldErrors.tenantId ? invalidInputClass : ""}
                 data-testid="adjustment-tenant" />
          <FieldError message={err("tenantId")} testId="adjustment-tenant-error" />
        </div>

        <div>
          <Label className={MUTED}>{t("gw.console.adjustments.direction")}</Label>
          <div className="flex gap-4 mt-2 text-sm">
            {["credit", "debit"].map((d) => (
              <label key={d} className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="direction" value={d}
                       checked={form.direction === d}
                       onChange={() => set("direction", d)}
                       data-testid={`adjustment-direction-${d}`} />
                <span>{t(`gw.console.adjustments.${d}`)}</span>
              </label>
            ))}
          </div>
          <FieldError message={err("direction")} testId="adjustment-direction-error" />
        </div>

        <div>
          <Label className={MUTED}>
            {t("gw.console.adjustments.amount", { currency: currencyLabel })}
          </Label>
          {/* Dollars in, cents on the wire (`toCents`). Money inputs are never cents. */}
          <Input type="number" inputMode="decimal" min="0" step="0.01"
                 value={form.amount} onChange={(e) => set("amount", e.target.value)}
                 className={fieldErrors.amount ? invalidInputClass : ""}
                 data-testid="adjustment-amount" />
          <FieldError message={err("amount")} testId="adjustment-amount-error" />
        </div>

        <div className="sm:col-span-2">
          <Label className={MUTED}>{t("gw.console.adjustments.reason")}</Label>
          <Textarea rows={2} value={form.reason}
                    onChange={(e) => set("reason", e.target.value)}
                    className={fieldErrors.reason ? invalidInputClass : ""}
                    data-testid="adjustment-reason" />
          <FieldError message={err("reason")} testId="adjustment-reason-error" />
          <p className={MUTED}>{t("gw.console.adjustments.reasonHint")}</p>
        </div>
      </div>

      <WriteError code={writeCode} testId="adjustment-write-error" />

      {/* `disabled` for STRUCTURAL blockers only, never for invalid content — a button greyed
          out because a field is empty states no reason the operator can act on. */}
      <Button onClick={openConfirm} data-testid="adjustment-open">
        {t("gw.console.adjustments.record")}
      </Button>

      <StepUpConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        titleKey="gw.console.adjustments.confirmTitle"
        descKey="gw.console.adjustments.confirmDesc"
        requireAck
        // The acknowledgement RESTATES the direction and the amount, because the direction is
        // the irreversible part and it is the one thing a signed field can silently get wrong.
        ackKey={form.direction === "debit"
          ? "gw.console.adjustments.ackDebit" : "gw.console.adjustments.ackCredit"}
        ackVars={{ tenant: form.tenantId.trim(),
                   amount: formatMoneyLedger(cents, settlementCurrency, lang) }}
        onConfirm={onConfirm}
        gatewayApi={gatewayApi}
      />
    </div>
  );
}
